/**
 * Liquid Effect — vanilla JS adaptation of threejs-components/liquid1
 * by Kevin Levron (@soju22). Wrapped for CurrentSeas (Alpine + vanilla).
 *
 * Subtle liquid distortion background. Mouse/touch/IMU reactive.
 */

const CDN = 'https://cdn.jsdelivr.net/npm/threejs-components@0.0.30/build/backgrounds/liquid1.min.js';

function generateTextImage(text, sub, tagline, bgColor, textColor, w, h) {
  const dpr = window.devicePixelRatio || 1;
  const off = document.createElement('canvas');
  off.width = w * dpr;
  off.height = h * dpr;
  const ctx = off.getContext('2d');
  if (!ctx) return null;
  ctx.scale(dpr, dpr);

  ctx.fillStyle = bgColor;
  ctx.fillRect(0, 0, w, h);

  // Soft ambient light for liquid reflections
  ctx.globalCompositeOperation = 'multiply';
  const g1 = ctx.createRadialGradient(w * 0.5, h * 0.1, 0, w * 0.5, h * 0.1, w * 0.6);
  g1.addColorStop(0, 'rgba(15, 30, 60, 0.6)');
  g1.addColorStop(1, 'rgba(10, 15, 25, 1)');
  ctx.fillStyle = g1;
  ctx.fillRect(0, 0, w, h);

  const g2 = ctx.createRadialGradient(w * 0.5, h * 0.95, 0, w * 0.5, h * 0.95, w * 0.5);
  g2.addColorStop(0, 'rgba(20, 60, 80, 0.4)');
  g2.addColorStop(1, 'rgba(10, 15, 25, 1)');
  ctx.fillStyle = g2;
  ctx.fillRect(0, 0, w, h);
  ctx.globalCompositeOperation = 'source-over';

  // Sub text
  if (sub) {
    ctx.fillStyle = textColor;
    ctx.globalAlpha = 0.3;
    ctx.font = `600 ${Math.max(11, w * 0.009)}px -apple-system, "SF Pro Display", Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText(sub.toUpperCase(), w / 2, h / 2 - w * 0.095);
  }

  // Main text
  const fs = Math.min(w * 0.12, h * 0.18);
  ctx.globalAlpha = 1;
  ctx.font = `700 ${fs}px -apple-system, "SF Pro Display", Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const lh = fs * 1.08;
  const sy = h / 2 - ((text?.length || 0) * lh) / 2 + lh / 2;
  text?.forEach((line, i) => { ctx.fillStyle = textColor; ctx.globalAlpha = 1; ctx.fillText(line, w / 2, sy + i * lh); });

  // Divider + tagline
  const dy = sy + (text?.length || 0) * lh + w * 0.018;
  if (text?.length && tagline) { ctx.globalAlpha = 0.1; ctx.fillStyle = textColor; ctx.fillRect(w / 2 - 30, dy, 60, 0.5); }
  if (tagline) { ctx.globalAlpha = 0.25; ctx.font = `400 ${Math.max(11, w * 0.01)}px -apple-system, "SF Pro Text", Arial, sans-serif`; ctx.fillText(tagline, w / 2, dy + w * 0.025); }

  return off.toDataURL('image/png');
}

export function liquidEffect(opts = {}) {
  const { text = [], sub = '', tagline = '', bgColor = '#0a0f1e', textColor = '#e2e8f0', opacity = 0.35 } = opts;

  return {
    app: null,
    _tiltX: 0, _tiltY: 0,
    _lastBeta: 0, _lastGamma: 0,

    async init(el) {
      const canvas = el.querySelector('canvas');
      if (!canvas) return;
      canvas.style.opacity = opacity;

      try {
        const mod = await import(CDN);
        const LiquidBackground = mod.default;
        const img = generateTextImage(text, sub, tagline, bgColor, textColor,
          el.offsetWidth || window.innerWidth, el.offsetHeight || window.innerHeight);

        this.app = LiquidBackground(canvas);
        if (img) this.app.loadImage(img);

        // Subtle liquid tuning
        if (this.app.liquidPlane?.material) {
          this.app.liquidPlane.material.metalness = 0.3;
          this.app.liquidPlane.material.roughness = 0.5;
        }
        if (this.app.liquidPlane?.uniforms?.displacementScale) {
          this.app.liquidPlane.uniforms.displacementScale.value = 1.5;
        }
        this.app.setRain(false);

        this._initIMU(canvas);
      } catch (e) {
        console.warn('liquid-effect: init failed', e);
      }
    },

    _initIMU(canvas) {
      if (typeof DeviceOrientationEvent === 'undefined') return;

      const self = this;
      const smoothing = 0.15;

      const handler = (e) => {
        const beta = e.beta || 0;
        const gamma = e.gamma || 0;
        self._lastBeta += (beta - self._lastBeta) * smoothing;
        self._lastGamma += (gamma - self._lastGamma) * smoothing;
        self._tiltX = Math.max(-1, Math.min(1, self._lastGamma / 45));
        self._tiltY = Math.max(-1, Math.min(1, self._lastBeta / 45));

        // Inject synthetic pointer event into the liquid renderer
        if (self.app?.renderer?.domElement) {
          const rect = canvas.getBoundingClientRect();
          const cx = (self._tiltX * 0.5 + 0.5) * rect.width + rect.left;
          const cy = (self._tiltY * 0.5 + 0.5) * rect.height + rect.top;
          const ev = new PointerEvent('pointermove', {
            clientX: cx, clientY: cy, bubbles: false
          });
          canvas.dispatchEvent(ev);
        }
      };

      const startIMU = async () => {
        if (typeof DeviceOrientationEvent.requestPermission === 'function') {
          try {
            const perm = await DeviceOrientationEvent.requestPermission();
            if (perm !== 'granted') return;
          } catch { return; }
        }
        window.addEventListener('deviceorientation', handler, { passive: true });
      };

      // iOS needs user gesture
      canvas.addEventListener('touchstart', () => startIMU(), { once: true });
      if (typeof DeviceOrientationEvent.requestPermission !== 'function') startIMU();
    },

    destroy() {
      if (this.app?.dispose) { this.app.dispose(); this.app = null; }
    }
  };
}